import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle, Flame, Pause, Play, Timer, Trophy, X } from "@phosphor-icons/react";
import { api, extractApiError } from "../lib/api";
import { useExerciseMap } from "../hooks/useExerciseMap";
import Layout from "../components/Layout";
import ExerciseInfoModal from "../components/builder/ExerciseInfoModal";

/** Parse the prescribed "weight" string into a number (lb).
 *  Handles "245 lb (70% 1RM)", "BW", "45 lb ruck", "60 lb DBs", etc. */
function parsePrescribedWeight(s) {
  if (!s) return null;
  const m = String(s).match(/(\d+(?:\.\d+)?)/);
  if (!m) return null;
  return parseFloat(m[1]);
}

/** Parse the prescribed reps into a target number (used as default for log).
 *  "5", "5-8", "8/side", "30s", "2 mi" -> first integer found. */
function parsePrescribedReps(s) {
  if (!s) return null;
  const m = String(s).match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : null;
}

function fmtTimer(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
              : `${m}:${String(s).padStart(2, "0")}`;
}

export default function WorkoutSession() {
  const { id, weekIndex, day } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState("");

  // Session state
  const [seconds, setSeconds] = useState(0);
  const [paused, setPaused] = useState(false);
  const [curExIdx, setCurExIdx] = useState(0);
  const [exerciseLogs, setExerciseLogs] = useState([]); // [{exercise_id, sets_performed:[{reps,weight_lb,rpe,completed}], notes}]
  const [sessionNotes, setSessionNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [doneResult, setDoneResult] = useState(null); // {prs, plan}
  const [infoExercise, setInfoExercise] = useState(null); // exercise object opened in How-to modal
  const exMap = useExerciseMap();
  const wkIdx = parseInt(weekIndex, 10);
  const dayNum = parseInt(day, 10);

  // Load plan + initialize empty log shells
  useEffect(() => {
    let active = true;
    api.get(`/plans/${id}`).then((r) => {
      if (!active) return;
      setPlan(r.data);
      const week = (r.data.weeks || []).find((w) => w.week_index === wkIdx);
      const d = week?.days?.find((dd) => dd.day === dayNum);
      const seeds = (d?.exercises || []).map((ex) => {
        const targetSets = ex.sets || 1;
        const seedW = parsePrescribedWeight(ex.weight);
        const seedR = parsePrescribedReps(ex.reps);
        return {
          exercise_id: ex.exercise_id,
          section: ex.section || "workout",
          prescribed: { sets: ex.sets, reps: ex.reps, weight: ex.weight, rpe: ex.rpe, notes: ex.notes },
          sets_performed: Array.from({ length: targetSets }).map(() => ({
            reps: seedR ?? null,
            weight_lb: seedW ?? null,
            rpe: ex.rpe ? parseFloat(String(ex.rpe).split("-")[0]) : null,
            completed: false,
          })),
          notes: "",
        };
      });
      setExerciseLogs(seeds);
    }).catch((e) => setErr(extractApiError(e)));
    return () => { active = false; };
  }, [id, wkIdx, dayNum]);

  // Session timer
  const tickRef = useRef(null);
  useEffect(() => {
    if (paused || doneResult) return;
    tickRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, [paused, doneResult]);

  const week = useMemo(() => plan?.weeks?.find((w) => w.week_index === wkIdx), [plan, wkIdx]);
  const dayObj = useMemo(() => week?.days?.find((dd) => dd.day === dayNum), [week, dayNum]);

  if (err) {
    return <Layout><div className="text-red-400">{err}</div></Layout>;
  }
  if (!plan || !dayObj) {
    return <Layout><div className="label-mono animate-pulse">LOADING SESSION...</div></Layout>;
  }

  // Skip-to next un-completed exercise (visual aid)
  const allSetsDone = (log) => log.sets_performed.every((s) => s.completed);
  const totalSetsDone = exerciseLogs.reduce((acc, l) => acc + l.sets_performed.filter((s) => s.completed).length, 0);
  const totalSets = exerciseLogs.reduce((acc, l) => acc + l.sets_performed.length, 0);
  const curLog = exerciseLogs[curExIdx];
  const curEx = curLog && exMap[curLog.exercise_id];

  const updateSet = (setIdx, patch) => {
    setExerciseLogs((logs) => logs.map((l, i) => {
      if (i !== curExIdx) return l;
      return {
        ...l,
        sets_performed: l.sets_performed.map((s, j) => (j === setIdx ? { ...s, ...patch } : s)),
      };
    }));
  };

  const addSet = () => {
    setExerciseLogs((logs) => logs.map((l, i) => {
      if (i !== curExIdx) return l;
      const last = l.sets_performed[l.sets_performed.length - 1] || {};
      return { ...l, sets_performed: [...l.sets_performed, { reps: last.reps, weight_lb: last.weight_lb, rpe: last.rpe, completed: false }] };
    }));
  };

  const removeSet = (setIdx) => {
    setExerciseLogs((logs) => logs.map((l, i) => {
      if (i !== curExIdx || l.sets_performed.length <= 1) return l;
      return { ...l, sets_performed: l.sets_performed.filter((_, j) => j !== setIdx) };
    }));
  };

  const updateExerciseNotes = (notes) => {
    setExerciseLogs((logs) => logs.map((l, i) => (i === curExIdx ? { ...l, notes } : l)));
  };

  const goNext = () => setCurExIdx((i) => Math.min(i + 1, exerciseLogs.length - 1));
  const goPrev = () => setCurExIdx((i) => Math.max(i - 1, 0));

  const finishSession = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Strip empty exercises (no completed sets)
      const payload = {
        week_index: wkIdx,
        day: dayNum,
        duration_seconds: seconds,
        notes: sessionNotes || null,
        exercises: exerciseLogs
          .filter((l) => l.sets_performed.some((s) => s.completed))
          .map((l) => ({
            exercise_id: l.exercise_id,
            notes: l.notes || null,
            sets_performed: l.sets_performed
              .filter((s) => s.completed)
              .map((s) => ({
                reps: s.reps != null ? Number(s.reps) : null,
                weight_lb: s.weight_lb != null ? Number(s.weight_lb) : null,
                rpe: s.rpe != null ? Number(s.rpe) : null,
                completed: true,
              })),
          })),
      };
      const { data } = await api.post(`/plans/${id}/workout-log`, payload);
      setDoneResult(data);
    } catch (e) {
      alert("Save failed: " + extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // -------- "Done" splash --------
  if (doneResult) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-12 text-center">
          <Flame size={72} weight="fill" className="text-volt mx-auto mb-6" />
          <div className="label-mono mb-2">// SESSION COMPLETE</div>
          <h1 className="font-display text-5xl font-black mb-3">CRUSHED IT.</h1>
          <div className="text-lg text-muted-foreground mb-8">
            {totalSetsDone} sets logged · {fmtTimer(seconds)} on the clock
          </div>
          {doneResult.prs?.length > 0 && (
            <div className="border border-volt p-6 mb-8 bg-volt/5" data-testid="pr-banner">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Trophy size={28} weight="fill" className="text-volt" />
                <div className="font-display text-2xl font-black uppercase">
                  {doneResult.prs.length === 1 ? "New Personal Record" : `${doneResult.prs.length} Personal Records`}
                </div>
              </div>
              <div className="space-y-3">
                {doneResult.prs.map((pr, i) => (
                  <div key={i} className="flex items-center justify-between text-left border-t border-volt/30 pt-3" data-testid={`pr-row-${i}`}>
                    <div>
                      <div className="font-display text-lg font-black">{pr.lift}</div>
                      <div className="text-xs text-muted-foreground">
                        {pr.weight_lb} lb × {pr.reps} → est. 1RM {pr.est_1rm_lb} lb
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-volt font-display text-2xl font-black">+{pr.improvement_lb} lb</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">est. 1RM</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground mt-4">
                Your assessment 1RMs have been auto-updated.
              </div>
            </div>
          )}
          <div className="flex justify-center gap-3">
            <button
              onClick={() => navigate(`/plans/${id}`)}
              className="bg-volt text-black px-6 py-3 font-display uppercase tracking-widest text-sm hover:opacity-90"
              data-testid="back-to-plan"
            >
              Back to Plan
            </button>
            <button
              onClick={() => navigate("/log")}
              className="border border-border px-6 py-3 font-display uppercase tracking-widest text-sm hover:border-volt hover:text-volt"
            >
              Workout Log
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // -------- Active session --------
  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        {/* Top bar: timer + progress + close.
            Sticky under the Layout header (z-40, ~68px desktop / ~112px mobile)
            so the timer and exit X stay visible during long workouts without
            colliding with the main app nav. */}
        <div className="sticky top-[112px] md:top-[68px] z-30 bg-background border-b border-border -mx-6 px-6 py-3 mb-6 flex items-center justify-between gap-3">
          <button onClick={() => { if (window.confirm("Quit this session? Logged sets will be lost.")) navigate(-1); }} className="text-muted-foreground hover:text-foreground" data-testid="session-close">
            <X size={20} weight="bold" />
          </button>
          <div className="flex-1 flex items-center gap-3 justify-center">
            <Timer size={16} weight="bold" className="text-volt" />
            <div className="font-mono font-bold text-lg tabular-nums" data-testid="session-timer">{fmtTimer(seconds)}</div>
            <button onClick={() => setPaused((p) => !p)} className="text-muted-foreground hover:text-volt" title={paused ? "Resume" : "Pause"} data-testid="pause-toggle">
              {paused ? <Play size={16} weight="bold" /> : <Pause size={16} weight="bold" />}
            </button>
          </div>
          <div className="font-display text-sm font-black tabular-nums">{totalSetsDone}/{totalSets}</div>
        </div>

        <div className="mb-4">
          <div className="label-mono text-muted-foreground">// {plan.name}</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black leading-tight">
            WK {wkIdx} · {dayObj.label || `DAY ${dayNum}`} · {dayObj.focus}
          </h1>
        </div>

        {/* Exercise tabs row — grouped by section (Warm-up / Workout / Cool-down) */}
        {(() => {
          const sectionMeta = {
            warmup: { label: "WARM-UP", color: "text-orange-400" },
            workout: { label: "WORKOUT", color: "text-volt" },
            cooldown: { label: "COOL-DOWN", color: "text-sky-400" },
          };
          const sectionOrder = ["warmup", "workout", "cooldown"];
          const grouped = { warmup: [], workout: [], cooldown: [] };
          exerciseLogs.forEach((log, i) => {
            const s = grouped[log.section] ? log.section : "workout";
            grouped[s].push(i);
          });
          const visible = sectionOrder.filter((s) => grouped[s].length > 0);
          return (
            <div className="mb-4" data-testid="exercise-tabs">
              {visible.map((s) => (
                <div key={s} className="mb-2">
                  {visible.length > 1 && (
                    <div className={`label-mono text-[10px] mb-1 ${sectionMeta[s].color}`}>// {sectionMeta[s].label}</div>
                  )}
                  <div className="flex gap-1 overflow-x-auto pb-1">
                    {grouped[s].map((i) => {
                      const log = exerciseLogs[i];
                      const done = allSetsDone(log);
                      return (
                        <button
                          key={i}
                          onClick={() => setCurExIdx(i)}
                          className={`shrink-0 px-3 py-1.5 text-xs uppercase tracking-widest border whitespace-nowrap ${
                            i === curExIdx ? "border-volt text-volt" : done ? "border-volt/40 text-muted-foreground" : "border-border text-muted-foreground"
                          }`}
                          data-testid={`tab-${i}`}
                        >
                          {done && <CheckCircle size={10} weight="fill" className="inline mr-1" />}
                          {(exMap[log.exercise_id]?.name || log.exercise_id).slice(0, 14)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Current exercise card */}
        {curLog && (
          <div className="border border-border bg-card p-5 mb-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="label-mono text-volt">// EXERCISE {curExIdx + 1} OF {exerciseLogs.length}</div>
                <h2 className="font-display text-2xl sm:text-3xl font-black uppercase leading-tight mt-1">
                  {curEx?.name || curLog.exercise_id}
                </h2>
                <div className="text-xs text-muted-foreground mt-2">
                  {curLog.prescribed.sets ? `${curLog.prescribed.sets}× ` : ""}
                  {curLog.prescribed.reps || ""}{curLog.prescribed.weight ? ` @ ${curLog.prescribed.weight}` : ""}
                  {curLog.prescribed.rpe ? ` · RPE ${curLog.prescribed.rpe}` : ""}
                </div>
                {curLog.prescribed.notes && (
                  <div className="text-xs text-volt mt-1 italic">{curLog.prescribed.notes}</div>
                )}
              </div>
              {curEx && (
                <button
                  onClick={() => setInfoExercise(curEx)}
                  className="text-muted-foreground hover:text-volt text-xs uppercase tracking-widest underline shrink-0"
                  title="View instructions"
                  data-testid="session-how-to"
                >
                  How to
                </button>
              )}
            </div>

            {/* Set table */}
            <div className="space-y-2 mb-4" data-testid="set-table">
              <div className="grid grid-cols-[40px_1fr_1fr_60px_44px] gap-2 text-[10px] uppercase tracking-widest text-muted-foreground px-1">
                <div>Set</div>
                <div>Reps</div>
                <div>Weight (lb)</div>
                <div>RPE</div>
                <div></div>
              </div>
              {curLog.sets_performed.map((s, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-[40px_1fr_1fr_60px_44px] gap-2 items-center px-1 py-2 border ${
                    s.completed ? "border-volt/60 bg-volt/5" : "border-border"
                  }`}
                  data-testid={`set-row-${i}`}
                >
                  <div className="font-display font-black">#{i + 1}</div>
                  <input
                    type="number" inputMode="decimal"
                    value={s.reps ?? ""}
                    onChange={(e) => updateSet(i, { reps: e.target.value === "" ? null : parseFloat(e.target.value) })}
                    placeholder="0" min={0} max={500}
                    className="bg-secondary border border-border px-2 py-1.5 text-sm w-full min-w-0"
                    data-testid={`set-reps-${i}`}
                  />
                  <input
                    type="number" inputMode="decimal"
                    value={s.weight_lb ?? ""}
                    onChange={(e) => updateSet(i, { weight_lb: e.target.value === "" ? null : parseFloat(e.target.value) })}
                    placeholder="0" min={0}
                    className="bg-secondary border border-border px-2 py-1.5 text-sm w-full min-w-0"
                    data-testid={`set-weight-${i}`}
                  />
                  <input
                    type="number" inputMode="decimal"
                    value={s.rpe ?? ""}
                    onChange={(e) => updateSet(i, { rpe: e.target.value === "" ? null : parseFloat(e.target.value) })}
                    placeholder="8" min={1} max={10} step={0.5}
                    className="bg-secondary border border-border px-2 py-1.5 text-sm w-full min-w-0"
                    data-testid={`set-rpe-${i}`}
                  />
                  <button
                    onClick={() => updateSet(i, { completed: !s.completed })}
                    className={`flex items-center justify-center h-full ${s.completed ? "text-volt" : "text-muted-foreground hover:text-volt"}`}
                    data-testid={`set-toggle-${i}`}
                    aria-label={s.completed ? "Unmark complete" : "Mark complete"}
                  >
                    <CheckCircle size={22} weight={s.completed ? "fill" : "regular"} />
                  </button>
                </div>
              ))}
              <div className="flex justify-between text-xs">
                <button onClick={addSet} className="text-volt hover:underline" data-testid="add-set-btn">+ Add Set</button>
                {curLog.sets_performed.length > 1 && (
                  <button onClick={() => removeSet(curLog.sets_performed.length - 1)} className="text-muted-foreground hover:text-destructive">– Remove Last</button>
                )}
              </div>
            </div>

            <textarea
              value={curLog.notes}
              onChange={(e) => updateExerciseNotes(e.target.value)}
              rows={2}
              placeholder="Exercise notes (optional)…"
              className="w-full bg-secondary border border-border px-3 py-2 text-sm focus:outline-none focus:border-volt resize-none"
            />
          </div>
        )}

        {/* Bottom nav */}
        <div className="flex items-center justify-between gap-3 mb-8">
          <button
            onClick={goPrev}
            disabled={curExIdx === 0}
            className="border border-border px-4 py-2 inline-flex items-center gap-1 text-xs uppercase tracking-widest disabled:opacity-30 hover:border-volt hover:text-volt"
            data-testid="prev-exercise"
          >
            <ArrowLeft size={12} weight="bold" /> Prev
          </button>
          {curExIdx < exerciseLogs.length - 1 ? (
            <button
              onClick={goNext}
              className="bg-volt text-black px-6 py-2 font-display uppercase tracking-widest text-xs hover:opacity-90 inline-flex items-center gap-1"
              data-testid="next-exercise"
            >
              Next <ArrowRight size={12} weight="bold" />
            </button>
          ) : (
            <button
              onClick={finishSession}
              disabled={busy || totalSetsDone === 0}
              className="bg-volt text-black px-6 py-3 font-display uppercase tracking-widest text-sm hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="finish-session-btn"
            >
              {busy ? "Saving..." : "Finish Session"} <Flame size={14} weight="fill" />
            </button>
          )}
        </div>

        <div className="mb-12">
          <label className="label-mono mb-1 block">// SESSION NOTES (OPTIONAL)</label>
          <textarea
            value={sessionNotes}
            onChange={(e) => setSessionNotes(e.target.value)}
            rows={2}
            placeholder="Overall how did the session feel?"
            className="w-full bg-secondary border border-border px-3 py-2 text-sm focus:outline-none focus:border-volt resize-none"
            data-testid="session-notes"
          />
        </div>
      </div>
      {infoExercise && (
        <ExerciseInfoModal
          exercise={infoExercise}
          exMap={exMap}
          onClose={() => setInfoExercise(null)}
        />
      )}
    </Layout>
  );
}
